import React, { useState } from 'react';

/**
 * Emergency Plan Guidance Component
 * Provides specialized guidance for emergency/contingency planning tasks
 */
function EmergencyPlanGuidance({ task, fileInputRef, uploading, files, onFileUpload }) {
  const [showUpload, setShowUpload] = useState(false);
  const hasFiles = files && files.length > 0;

  const planElements = [
    { title: 'Varslingsliste', description: 'Hvem skal kontaktes ved ulike hendelser', priority: 'Kritisk' },
    { title: 'Evakueringsplan', description: 'Rømningsveier og møteplass', priority: 'Kritisk' },
    { title: 'Førstehjelp', description: 'Førstehjelpsansvarlige og utstyrplassering', priority: 'Høy' },
    { title: 'Brannvern', description: 'Slukkeutstyr og brannvernleder', priority: 'Høy' },
    { title: 'Mediehåndtering', description: 'Hvem uttaler seg til media', priority: 'Middels' },
    { title: 'Gjenoppretting', description: 'Plan for å komme tilbake til normal drift', priority: 'Middels' },
  ];

  const scenarios = [
    'Brann', 'Personskade', 'Trussel/vold', 'Strømbrudd', 'Vannlekkasje', 
    'IT-havari', 'Smitteutbrudd', 'Naturhendelse'
  ];

  return (
    <div data-testid="emergency-plan-guidance">
      {/* Why this is important */}
      <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
        <div className="flex gap-3">
          <div className="flex-shrink-0 w-8 h-8 bg-red-100 rounded-full flex items-center justify-center">
            <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <div>
            <p className="text-sm font-semibold text-red-900 mb-1">Når krisen inntreffer</p>
            <p className="text-sm text-red-800">
              En god beredskapsplan sikrer at alle vet hva de skal gjøre når det haster. 
              Uten plan kan sekunder gjøre forskjellen mellom en håndterbar hendelse og en katastrofe.
            </p>
          </div>
        </div>
      </div>

      {/* What to include */}
      <p className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-3">
        Beredskapsplanen bør inneholde
      </p>

      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-4">
        <div className="space-y-2">
          {planElements.map((element, idx) => (
            <div key={idx} className="flex items-center justify-between p-2 bg-white rounded-lg border border-blue-100">
              <div>
                <p className="text-sm font-medium text-gray-900">{element.title}</p>
                <p className="text-xs text-gray-500">{element.description}</p>
              </div>
              <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                element.priority === 'Kritisk' ? 'bg-red-100 text-red-700' :
                element.priority === 'Høy' ? 'bg-amber-100 text-amber-700' :
                'bg-gray-100 text-gray-600'
              }`}>
                {element.priority}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Scenarios to consider */}
      <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 mb-4">
        <h4 className="font-semibold text-gray-900 mb-3">Scenarier å vurdere</h4>
        <div className="flex flex-wrap gap-2">
          {scenarios.map((scenario, idx) => (
            <span key={idx} className="px-3 py-1 bg-white border border-gray-200 rounded-full text-sm text-gray-700">
              {scenario}
            </span>
          ))}
        </div>
      </div>

      {/* Tips */}
      <div className="bg-green-50 border border-green-200 rounded-xl p-4 mb-4">
        <h4 className="font-semibold text-green-900 mb-2 flex items-center gap-2">
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
          </svg>
          Tips
        </h4>
        <ul className="text-sm text-green-800 space-y-1">
          <li>• Planen bør være enkel og lett å finne frem i under stress</li>
          <li>• Heng opp varslingsliste og evakueringsplan synlig</li>
          <li>• Øv på planen minst årlig (brannøvelse)</li>
          <li>• Oppdater kontaktinformasjon regelmessig</li>
        </ul>
      </div>

      {/* Upload option */}
      <div className="border border-gray-200 rounded-xl p-4">
        <button
          onClick={() => setShowUpload(!showUpload)}
          className="w-full flex items-center gap-3 text-left"
        >
          <div className="flex-shrink-0 w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
            <svg className="w-6 h-6 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
          <div className="flex-1">
            <span className="font-medium text-gray-900">Last opp beredskapsplan</span>
            <p className="text-sm text-gray-500 mt-0.5">
              Har du allerede en beredskapsplan?
            </p>
          </div>
          <svg className={`w-5 h-5 text-gray-400 transition-transform ${showUpload ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        {showUpload && (
          <div className="mt-4 pt-4 border-t border-gray-100">
            <button
              onClick={() => fileInputRef?.current?.click()}
              disabled={uploading}
              className="w-full px-4 py-3 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {uploading ? 'Laster opp...' : 'Last opp dokument'}
            </button>
            {hasFiles && (
              <p className="text-xs text-green-600 mt-2 text-center">
                ✓ {files.length} dokument(er) lastet opp
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default EmergencyPlanGuidance;
